(() => {
  const initSearchDrawer = (drawer) => {
    if (!drawer || drawer.dataset.customSearchReady === 'true') return;
    drawer.dataset.customSearchReady = 'true';

    const featured = drawer.querySelector('[data-search-featured]');
    const triggerSelector = '[aria-controls="search-drawer"], [aria-controls="sb-search-drawer"], store-header a[href^="/search"]';
    let featuredRequest;

    const loadFeaturedProducts = () => {
      if (!featured?.dataset.featuredUrl) return Promise.resolve();
      if (featuredRequest) return featuredRequest;

      const loading = featured.querySelector('[data-search-featured-loading]');
      const errorMessage = featured.querySelector('[data-search-featured-error]');
      loading.hidden = false;
      errorMessage.hidden = true;
      featured.setAttribute('aria-busy', 'true');

      featuredRequest = (async () => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          const response = await fetch(featured.dataset.featuredUrl, { signal: controller.signal });
          if (!response.ok) throw new Error('Unable to load featured products.');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = html.querySelector('[data-search-featured]');
          if (!content || content.hasAttribute('data-featured-url')) {
            throw new Error('Featured products were not rendered.');
          }
          // Only replace idle content so an in-flight search keeps its results and focus.
          featured.replaceChildren(...content.childNodes);
          delete featured.dataset.featuredUrl;
        } catch (error) {
          loading.hidden = true;
          errorMessage.hidden = false;
        } finally {
          window.clearTimeout(timeout);
          featured.removeAttribute('aria-busy');
          featuredRequest = null;
        }
      })();
      return featuredRequest;
    };

    const syncPageScrollLock = () => {
      document.documentElement.classList.toggle('custom-search-open', drawer.hasAttribute('open'));
      if (drawer.hasAttribute('open')) loadFeaturedProducts();
    };

    drawer.addEventListener('toggle', syncPageScrollLock);
    drawer.addEventListener('dialog:after-show', syncPageScrollLock);
    drawer.addEventListener('dialog:after-hide', syncPageScrollLock);
    new MutationObserver(syncPageScrollLock).observe(drawer, {
      attributes: true,
      attributeFilter: ['open']
    });
    syncPageScrollLock();

    let openingSearch;
    const openSearchDrawer = (event) => {
      const trigger = event.target.closest(triggerSelector);
      if (!trigger || drawer.contains(trigger)) return;

      event.preventDefault();
      event.stopPropagation();
      trigger.setAttribute('aria-controls', 'search-drawer');
      trigger.setAttribute('aria-busy', 'true');
      loadFeaturedProducts();

      if (!openingSearch) {
        openingSearch = (async () => {
          if (window.customElements?.whenDefined) {
            await window.customElements.whenDefined('search-drawer');
          }
          if (typeof drawer.show === 'function') await drawer.show();
          else drawer.setAttribute('open', '');
          drawer.querySelector('.custom-search__input')?.focus({ preventScroll: true });
        })().catch((error) => {
          console.error('Search drawer could not open:', error);
        }).finally(() => {
          openingSearch = null;
          document.querySelectorAll(triggerSelector).forEach((button) => button.removeAttribute('aria-busy'));
        });
      }
    };

    document.querySelectorAll('[aria-controls="sb-search-drawer"]').forEach((trigger) => {
      trigger.setAttribute('aria-controls', 'search-drawer');
    });
    document.addEventListener('click', openSearchDrawer, true);
    const preloadFeaturedProducts = (event) => {
      if (event.target.closest(triggerSelector) && !drawer.contains(event.target)) loadFeaturedProducts();
    };
    document.addEventListener('pointerover', preloadFeaturedProducts);
    document.addEventListener('focusin', preloadFeaturedProducts);

    drawer.addEventListener('change', (event) => {
      const select = event.target.closest('[data-search-variant-select]');
      if (!select) return;

      const card = select.closest('[data-search-product-card]');
      const addButton = card?.querySelector('[data-search-one-time], .custom-search-card__add');
      const purchaseChoices = card?.querySelector('[data-search-purchase-choices]');
      const subscriptionToggle = card?.querySelector('[data-search-subscription-toggle]');
      const subscriptionLabel = card?.querySelector('[data-search-subscription-label]');
      const subscriptionMenu = card?.querySelector('[data-search-subscription-menu]');
      const currentPrice = card?.querySelector('[data-search-card-current-price]');
      const comparePrice = card?.querySelector('[data-search-card-compare-price]');
      const option = select.selectedOptions[0];
      const hasSubscription = option?.dataset.hasSubscription === 'true';
      const requiresSellingPlan = option?.dataset.requiresSellingPlan === 'true';

      if (addButton) addButton.dataset.variantId = select.value;
      if (currentPrice && option?.dataset.price) currentPrice.textContent = option.dataset.price;
      if (comparePrice) {
        comparePrice.textContent = option?.dataset.comparePrice || '';
        comparePrice.hidden = option?.dataset.onSale !== 'true';
      }
      if (addButton) {
        addButton.hidden = requiresSellingPlan;
        addButton.disabled = !select.value || option?.disabled;
        addButton.querySelector('[data-search-add-label]').textContent = option?.disabled
          ? 'Sold out'
          : (addButton.hasAttribute('data-search-one-time') ? 'Add' : 'Add to cart');
      }
      if (subscriptionToggle) subscriptionToggle.hidden = !hasSubscription;
      if (subscriptionLabel && option?.dataset.subscriptionLabel) {
        subscriptionLabel.textContent = option.dataset.subscriptionLabel;
      }
      purchaseChoices?.classList.toggle('is-subscription-only', requiresSellingPlan);
      card?.querySelectorAll('.custom-search-card__subscription-option').forEach((button) => {
        button.hidden = button.dataset.variantId !== select.value;
      });
      if (subscriptionMenu) subscriptionMenu.hidden = true;
      subscriptionToggle?.setAttribute('aria-expanded', 'false');
    });

    drawer.addEventListener('click', async (event) => {
      if (event.target.closest('[data-search-featured-retry]')) {
        loadFeaturedProducts();
        return;
      }
      const subscriptionToggle = event.target.closest('[data-search-subscription-toggle]');
      if (subscriptionToggle) {
        const card = subscriptionToggle.closest('[data-search-product-card]');
        const subscriptionMenu = card?.querySelector('[data-search-subscription-menu]');
        if (!subscriptionMenu) return;

        subscriptionMenu.hidden = !subscriptionMenu.hidden;
        subscriptionToggle.setAttribute('aria-expanded', String(!subscriptionMenu.hidden));
        return;
      }

      const addButton = event.target.closest('[data-search-add-to-cart]');
      if (!addButton || addButton.disabled || addButton.getAttribute('aria-busy') === 'true') return;

      const variantId = addButton.dataset.variantId;
      const sellingPlanId = addButton.dataset.sellingPlanId;
      if (!variantId) return;

      const label = addButton.querySelector('[data-search-add-label]');
      const originalLabel = label?.textContent || 'Add to cart';
      addButton.setAttribute('aria-busy', 'true');
      if (label) label.textContent = 'Adding…';

      try {
        const sections = ['variant-added'];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections }
        }));

        const formData = new FormData();
        formData.set('id', variantId);
        formData.set('quantity', '1');
        if (sellingPlanId) formData.set('selling_plan', sellingPlanId);
        formData.set('sections', sections.join(','));
        formData.set('sections_url', `${window.Shopify.routes.root}variants/${variantId}`);

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const responseJson = await response.json();
        if (!response.ok) throw new Error(responseJson.description || 'Unable to add this item.');

        const cart = await fetch(`${window.Shopify.routes.root}cart.js`).then((cartResponse) => cartResponse.json());
        cart.sections = responseJson.sections;

        addButton.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        }));
      } catch (error) {
        console.error('Search add to cart failed:', error);
        if (label) label.textContent = 'Try again';
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
        }, 1600);
      } finally {
        addButton.removeAttribute('aria-busy');
      }
    });

    drawer.addEventListener('variant:add', () => {
      if (typeof drawer.hide === 'function') drawer.hide();
    });

    drawer.querySelector('.custom-search__menu-button')?.addEventListener('click', () => {
      if (typeof drawer.hide === 'function') drawer.hide();
    });

    drawer.querySelector('.custom-search__cart-link')?.addEventListener('click', () => {
      if (typeof drawer.hide === 'function') drawer.hide();
    });

    document.documentElement.setAttribute('data-search-handler-ready', '');
    const pending = document.querySelector('[data-search-pending]');
    document.querySelectorAll('[data-search-pending]').forEach((trigger) => trigger.removeAttribute('data-search-pending'));
    pending?.click();
  };

  const init = () => initSearchDrawer(document.querySelector('.custom-search-drawer'));

  if (!document.querySelector('.custom-search-drawer') && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
